import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  applyForLocker,
  joinLockerWaitlist,
  lockerAvailability,
  lockers,
  recordLockerVisit,
  setLockerAutoDebit,
  surrenderLocker,
  LOCKER_SIZES,
} from '@/api/bankServices';
import type { Locker, LockerBranch, LockerSize, LockerWaitlist } from '@/types/phase4.types';
import { usePaymentAuth } from '@/pages/Pay/components/usePaymentAuth';
import PaymentAuthSheets from '@/pages/Pay/components/PaymentAuthSheets';
import { formatIndianCurrency } from '@/utils/currency';
import '../phase4.css';

const Lockers = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState<LockerBranch[]>([]);
  const [mine, setMine] = useState<Locker[]>([]);
  const [waitlist, setWaitlist] = useState<LockerWaitlist[]>([]);
  const [branchId, setBranchId] = useState('');
  const [size, setSize] = useState<LockerSize>('SMALL');
  const [joint, setJoint] = useState(false);
  const [secondHirer, setSecondHirer] = useState('');
  const [nominee, setNominee] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([lockerAvailability(), lockers()])
      .then(([available, current]) => {
        setBranches(available);
        setMine(current.lockers);
        setWaitlist(current.waitlist);
        if (!branchId && available[0]) setBranchId(available[0].id);
      })
      .catch(() => toast.error('Could not load locker details.'));
  };
  useEffect(load, []);

  const auth = usePaymentAuth(async () => {
    setBusy(true);
    try {
      await applyForLocker({
        branchId,
        size,
        joint,
        secondHirer: joint ? secondHirer : undefined,
        mode: joint ? 'EITHER_OR_SURVIVOR' : undefined,
        nominees: nominee ? [{ name: nominee, relationship: 'Nominee' }] : [],
      });
      toast.success('Locker allotted');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not apply for locker.');
    } finally {
      setBusy(false);
    }
  });

  const apply = async () => {
    const error = await auth.start('NOVABANK', 'Locker application');
    if (error) toast.error(error);
  };

  const join = async () => {
    try {
      const place = await joinLockerWaitlist(branchId, size);
      toast.success(`Waitlist number ${place.waitNumber}`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not join waitlist.');
    }
  };

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" aria-label="Back" title="Back" onClick={() => navigate('/profile/bank')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Reserve a Locker</h2>
      </div>

      {mine.map((locker) => (
        <div className="nova-p4-card" key={locker.id}>
          <h3>Locker {locker.number}</h3>
          <div className="nova-p4-moneyline muted"><span>Branch</span><span>{branches.find((b) => b.id === locker.branchId)?.name ?? locker.branchId}</span></div>
          <div className="nova-p4-moneyline muted"><span>Size</span><span>{LOCKER_SIZES[locker.size].label}</span></div>
          <div className="nova-p4-moneyline muted"><span>Annual rent</span><span>{formatIndianCurrency(locker.rentRupees)}</span></div>
          <label className="d-flex gap-2 align-items-center mb-3">
            <input type="checkbox" checked={locker.autoDebit} onChange={() => void setLockerAutoDebit(locker.id, !locker.autoDebit).then(load)} />
            Auto-debit annual rent
          </label>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="nova-p4-secondary" onClick={() => void recordLockerVisit(locker.id).then(() => toast.success('Visit recorded'))}>Record branch visit</button>
            <button type="button" className="nova-p4-secondary" onClick={() => {
              if (window.confirm('Surrender this locker?')) void surrenderLocker(locker.id).then((r) => { toast.success(`Locker surrendered. Refund ${formatIndianCurrency(r.refundRupees)}`); load(); }).catch((e) => toast.error(e.message));
            }}>Surrender locker</button>
          </div>
        </div>
      ))}

      {waitlist.map((place) => (
        <div className="nova-p4-banner blue" key={place.id}>Waitlist #{place.waitNumber} for {LOCKER_SIZES[place.size].label} locker at {place.branchId}.</div>
      ))}

      <div className="nova-p4-card">
        <h3>Apply for a locker</h3>
        <div className="nova-p4-field"><label htmlFor="locker-branch">Branch</label><select id="locker-branch" className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)}>{branches.map((b) => <option value={b.id} key={b.id}>{b.name}, {b.city}</option>)}</select></div>
        <div className="nova-p4-field"><label htmlFor="locker-size">Size</label><select id="locker-size" className="form-control" value={size} onChange={(e) => setSize(e.target.value as LockerSize)}>{(Object.keys(LOCKER_SIZES) as LockerSize[]).map((key) => <option value={key} key={key}>{LOCKER_SIZES[key].label} — {formatIndianCurrency(LOCKER_SIZES[key].rentRupees)}/year</option>)}</select></div>
        <label className="d-flex gap-2 align-items-center mb-3"><input type="checkbox" checked={joint} onChange={(e) => setJoint(e.target.checked)} /> Joint hirer</label>
        {joint && <div className="nova-p4-field"><label htmlFor="locker-hirer">Second hirer</label><input id="locker-hirer" className="form-control" value={secondHirer} onChange={(e) => setSecondHirer(e.target.value)} /></div>}
        <div className="nova-p4-field"><label htmlFor="locker-nominee">Nominee (optional)</label><input id="locker-nominee" className="form-control" value={nominee} onChange={(e) => setNominee(e.target.value)} /></div>
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="nova-p4-primary" disabled={busy || !branchId || (joint && !secondHirer)} onClick={() => void apply()}>Apply with OTP</button>
          <button type="button" className="nova-p4-secondary" disabled={!branchId} onClick={() => void join()}>Join waitlist</button>
        </div>
      </div>
      <PaymentAuthSheets authState={auth.authState} approvalLine="Locker application" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </div>
  );
};

export default Lockers;
